import * as React from 'react';
import cx from 'classnames';
import px from 'prop-types';
import { Location } from 'Common/utils';
import { useTranslation } from 'Common/hooks';
import { Spinner } from 'Common/components/ui';

const LocationSearch = React.forwardRef(function LocationSearchComponent(
    {
        children,
        className,
        google,
        name,
        onClearInput,
        onPlaceChange,
        onRequestLocation,
        options = {},
        placeholder,
        type = 'text',
        value,
        ...rest
    },
    inputRef
) {
    const autoComplete = React.useRef(null);
    const optionsRef = React.useRef(options);
    const defaultInputRef = React.useRef();
    const resolvedInputRef = inputRef ?? defaultInputRef;
    const locationTip = useTranslation('Dealer.Locator.Search.CurrentLocation.ToolTip');
    const clearTip = useTranslation('Dealer.Locator.Search.ClearInput.ToolTip');

    const onChange = React.useCallback(() => {
        const nextPlace = autoComplete.current.getPlace();

        if (nextPlace?.geometry && onPlaceChange) {
            onPlaceChange(Location.addressFromPlace(nextPlace));
        }
    }, [onPlaceChange]);

    const onChangeRef = React.useRef(onChange);

    React.useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    React.useEffect(() => {});

    React.useEffect(() => {
        function handler(e) {
            return onChangeRef.current(e);
        }

        if (google && !autoComplete.current) {
            const el = resolvedInputRef.current;

            autoComplete.current = new google.maps.places.Autocomplete(el, optionsRef.current);
            autoComplete.current.addListener('place_changed', handler);
            Location.ui.enableEnterKey(el, autoComplete.current);
            return () => google.maps.event.clearInstanceListeners(el);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- resolvedInputRef is stable
    }, [google]);

    return google ? (
        <div className={cx('LocationSearch', className)}>
            {onRequestLocation ? (
                <button
                    className="locateBtn searchBtn btn btn-sm"
                    title={locationTip}
                    onClick={onRequestLocation}
                    disabled={!onRequestLocation}
                >
                    <i className="fas fa-crosshairs" aria-hidden="true" />
                </button>
            ) : null}
            <input
                className="storeLocatorSearchInput"
                aria-label={placeholder || name}
                placeholder={placeholder}
                name={name}
                type={type}
                ref={resolvedInputRef}
                value={value}
                {...rest}
            />
            {value && onClearInput ? (
                <button className="clearBtn searchBtn btn btn-sm" title={clearTip} onClick={onClearInput}>
                    <i className="fas fa-times" aria-hidden="true" />
                </button>
            ) : null}
            {children}
        </div>
    ) : (
        <div className={cx('LocationSearch', className)}>
            <Spinner />
        </div>
    );
});

export default LocationSearch;

LocationSearch.propTypes = {
    children: px.node,
    className: px.string,
    google: px.objectOf(px.any),
    name: px.string,
    onClearInput: px.func,
    onPlaceChange: px.func,
    onRequestLocation: px.func,
    options: px.objectOf(px.any),
    placeholder: px.string,
    style: px.objectOf(px.any),
    type: px.string,
    value: px.string,
};
